import { AnnotatePanelProps } from "../annotate";
import React from "react";
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Separator,
  SheetHeader,
  SheetTitle,
} from "@mindstonehq/ui";
import CommentRow from "./comment-row";
import { motion } from "framer-motion";
import CommentInput from "./comment-input";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import Blockquote, { HighlightBlockquote } from "./blockquote";
import { IHighlightCommon } from "~/typings/annotate/annotation";
import useCommentMutations from "~/components/annotate/hooks/comment-mutations";
import { Trans, useLingui, Plural } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

export default function ResourceCommentsPanel({
  article,
  annotations,
  refresh,
}: AnnotatePanelProps) {
  const { t } = useLingui();
  const [text, setText] = React.useState("");
  const listRef = React.useRef<HTMLDivElement>(null);
  const [expandedHighlight, setExpandedHighlight] = React.useState<
    string | null
  >(null);
  const { addComment, deleteComment, isLoading } = useCommentMutations();

  const handleDelete = (id: string) => {
    return deleteComment(id, article.id).then((res) => {
      refresh();
    });
  };

  const createComment = () => {
    const highlight = annotations.highlights.find(
      (highlight) => highlight.id === expandedHighlight,
    );
    return addComment(text, highlight as IHighlightCommon, article.id).then(
      ({ commentID, highlight }) => {
        refresh();
        setText("");
        setTimeout(() => {
          scrollToBottom();
        }, 500);
        return commentID;
      },
    );
  };

  const scrollToBottom = () => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight + 500,
      behavior: "smooth",
    });
  };

  const privateCommentHighlights = annotations.highlights.filter(
    (h) => h.comments.length,
  );

  return (
    <div className="flex flex-col flex-1 h-full gap-6">
      <SheetHeader>
        <SheetTitle className="text-2xl flex items-center space-x-1">
          {expandedHighlight ? (
            <>
              <div
                className="hover:underline cursor-pointer"
                onClick={() => setExpandedHighlight(null)}
              >
                <Trans>All comments</Trans>
              </div>
              <div className="flex items-center text-placeholder-foreground">
                <ChevronRightIcon className="w-4 h-4" />
                <span><Trans>Comment thread</Trans></span>
              </div>
            </>
          ) : (
            <Trans>All comments</Trans>
          )}
        </SheetTitle>
        <Separator className="bg-highlight" />
      </SheetHeader>
      <div
        className="flex flex-col flex-1 overflow-y-auto px-6 -mr-6"
        ref={listRef}
      >
        {privateCommentHighlights.length > 0 ? (
          <div className="flex flex-col pr-6 space-y-6">
            {privateCommentHighlights.map((highlight) => (
              <motion.div
                key={highlight.id}
                animate={
                  expandedHighlight && expandedHighlight !== highlight.id
                    ? { opacity: 0, maxHeight: 0, overflow: "hidden" }
                    : { opacity: 1, maxHeight: 500 }
                }
              >
                <Collapsible
                  open={expandedHighlight === highlight.id}
                  className="flex flex-col"
                >
                  <div className="flex flex-col space-y-2">
                    <HighlightBlockquote
                      text={highlight?.text || ""}
                      model={highlight?.model}
                    />
                    <div className="flex flex-col pt-2">
                      {highlight.comments.slice(0, 3).map((comment, i) => (
                        <>
                          {i !== 0 && <Separator className="mb-6" />}
                          <CommentRow
                            key={comment.id}
                            comment={comment}
                            handleDelete={handleDelete}
                          />
                        </>
                      ))}
                    </div>
                    <CollapsibleContent className="flex flex-col">
                      {highlight.comments.slice(3).map((comment) => (
                        <div className="flex flex-col" key={comment.id}>
                          <Separator className="mb-6" />
                          <CommentRow
                            key={comment.id}
                            comment={comment}
                            handleDelete={handleDelete}
                          />
                        </div>
                      ))}
                    </CollapsibleContent>
                  </div>
                  <CollapsibleTrigger asChild className="flex justify-end">
                    {expandedHighlight !== highlight.id && (
                      <Button
                        variant="link"
                        className=""
                        onClick={() => setExpandedHighlight(highlight.id)}
                      >
                        {highlight.comments.length > 3 && (
                          <>
                            <Plural
                              value={highlight.comments.length - 3}
                              one="# more comment"
                              other="# more comments"
                            />
                            {" / "}
                          </>
                        )}
                        <Trans>Reply to thread</Trans>
                      </Button>
                    )}
                  </CollapsibleTrigger>
                </Collapsible>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="p-4"><Trans>No comments on this resource</Trans></div>
        )}
      </div>
      <motion.div
        animate={
          !!expandedHighlight
            ? // eslint-disable-next-line lingui/no-unlocalized-strings -- framer-motion CSS value
              { opacity: 1, maxHeight: "500px" }
            : // eslint-disable-next-line lingui/no-unlocalized-strings -- framer-motion CSS values
              { opacity: 0, maxHeight: "0px", overflow: "hidden" }
        }
      >
        <Separator className="mt-[6px] bg-highlight" />
        <div className="py-6">
          <CommentInput
            text={text}
            setText={setText}
            createComment={createComment}
          />
        </div>
        <Button
          variant="secondary"
          className="w-auto self-end"
          disabled={!text || isLoading}
          onClick={createComment}
        >
          <Trans>Post Comment</Trans>
        </Button>
      </motion.div>
    </div>
  );
}
